import { Button, Divider } from '@design-system';
import { useFeatureIsOn } from '@growthbook/growthbook-react';
import { useAnalyticsEmitter } from '@utils/analytics/emitter';
import { AnalyticsFlowProvider } from '@utils/analytics/flow';
import { formatArtistNames, renderShopifyArtistName } from '@utils/artists';
import { isInNextSixDays } from '@utils/product';
import { Trans, useTranslation } from 'next-i18next';
import React, { FC, useCallback, useState } from 'react';

import { useProductCheckoutLink } from '../../../../features/checkout/hooks/useProductCheckoutLink';
import { PreAuthDrawButton } from '../../../../features/draws/components/PreAuthDrawButton';
import { useDrawButtonLabels } from '../../../../features/draws/hooks/useDrawButtonLabels';
import { buildDrawFlowConfig } from '../../../../features/draws/utils/useDrawFlow';
import {
  getProductRef,
  getProductStatus,
} from '../../../../helpers/purchasing';
import { useClientSide } from '../../../../hooks/useClientSide';
import { useCurrency } from '../../../../hooks/useCurrency';
import { useIsPreOrder } from '../../../../hooks/useIsPreOrder';
import { useUrlHasParam } from '../../../../hooks/useUrlHasParam';
import { useFlow } from '../../../../providers';
import { useDraws } from '../../../../providers/DrawProvider';
import { usePreAuthDrawCtx } from '../../../../providers/PreAuthDrawProvider';
import { usePurchase } from '../../../../providers/PurchaseProvider';
import { SimpleDialog } from '../../../Dialog/SimpleDialog';
import { DialogFlowWithStripe } from '../../Dialog/DialogFlowWithStripe';
import ProductDate from '../../ProductDate';
import { SignUpButton } from '../SignUpButton';
import { toClientUTCDateFormat } from '../utils';
import { PreOrderHeader } from './PreOrderHeader';

interface Props {
  product: ProductType;
}

export const PreOrderFlow: FC<Props> = ({ product }) => {
  const { t } = useTranslation('product');
  const [showDialog, setShowDialog] = useState(false);
  const purchasingController = usePurchase();
  const hasCheckoutParam = useUrlHasParam('checkout_id');
  const isClient = useClientSide();
  const [currency] = useCurrency();
  const { startFlow } = useFlow();

  const {
    activeDraw,
    customerSelectedFrame,
    entryIntentResponse,
    resetController,
    setupSecret,
  } = usePreAuthDrawCtx();

  const [emitter] = useAnalyticsEmitter();

  const handleHowToClick = useCallback(() => {
    emitter('', 'how clicked', {
      type: 'pre-order',
    });
  }, [emitter]);

  const { mainDraw } = useDraws();

  const handleFlowClose = useCallback(() => {
    resetController();
  }, [resetController]);

  const isPreOrder = useIsPreOrder(mainDraw?.draw);

  const checkoutLink = useProductCheckoutLink();
  const labels = useDrawButtonLabels('mainCta', isPreOrder);

  const isFramedByDefaultFeatureEnabled = useFeatureIsOn('framed-by-default');

  if (!mainDraw) {
    return null;
  }

  const flowConfig = buildDrawFlowConfig(
    t,
    mainDraw,
    isPreOrder,
    isFramedByDefaultFeatureEnabled,
  );

  const launchDate = new Date(toClientUTCDateFormat(product.launchDate));

  return (
    <>
      <div className="flex w-full flex-col space-y-6">
        <PreOrderHeader
          active
          className="w-full"
          description={t('preOrder.isOpen.description', {
            count: mainDraw.draw.allocation,
          })}
          title={t('preOrder.isOpen.title')}
        />
        <div className="flex w-full flex-col items-center space-y-3">
          {checkoutLink ? (
            <Button
              as="a"
              fullWidth
              href={checkoutLink}
              label={t(labels.normal)}
              onClick={() => {
                emitter('draw entry', 'clicked', {
                  'product title': mainDraw.product.title,
                  'artist name': mainDraw.product.artists[0]?.name ?? '',
                  'product price': mainDraw.product.price,
                });
              }}
              size="lg"
              variant="primary"
            />
          ) : (
            <PreAuthDrawButton
              buttonLocation="mainCta"
              drawWithFrames={mainDraw}
              hasCheckoutParam={hasCheckoutParam}
              startDrawFlow={() => {
                startFlow({
                  ...flowConfig,
                  onFlowComplete: handleFlowClose,
                });
              }}
            />
          )}
          <button
            className="hyperlink"
            onClick={() => {
              setShowDialog(true);
              handleHowToClick();
            }}
            type="button"
          >
            {t('subscriptionCta.preOrder.learnMoreDialog.title')}
          </button>
        </div>

        <Divider variant="dashed" />

        <div className="flex flex-col text-center">
          <p className="font-bold">
            {isInNextSixDays(launchDate)
              ? t('subscriptionDate.default.countdown')
              : t('subscriptionDate.default.date')}
          </p>
          <p>
            <ProductDate date={launchDate} />
          </p>
        </div>

        <p className="text-center text-neutral-4">
          {t('preOrder.isOpen.titleAllocation', {
            count: mainDraw.draw.allocation,
          })}
        </p>

        {isClient && (
          <SignUpButton
            isPreOrder
            labels={{ normal: t('notifyMeAtLaunch'), success: t('subscribed') }}
            product={product}
            productRef={getProductRef(purchasingController)}
            productStatus={getProductStatus(product, purchasingController)}
            showIcon
            variant="secondary"
          />
        )}
      </div>

      <SimpleDialog
        onClose={() => setShowDialog(false)}
        open={showDialog}
        title={t('subscriptionCta.preOrder.learnMoreDialog.title')}
      >
        <p>
          <Trans i18nKey="subscriptionCta.preOrder.learnMoreDialog.paragraph1">
            When we produce a limited edition, we set aside a number for
            collectors to apply for pre-order ahead of the release day. You can
            read more on why we do this in&nbsp;
            <a className="hyperlink" href="/support/allocation" target="_blank">
              our FAQs
            </a>
            .
          </Trans>
        </p>
        <p className="my-3">
          {t('subscriptionCta.preOrder.learnMoreDialog.paragraph2')}
        </p>
        <p className="my-3">
          <Trans i18nKey="subscriptionCta.preOrder.learnMoreDialog.paragraph3">
            For each release, we reserve an allocation for pre-orders for
            different groups of collectors: some for first-time collectors, some
            for existing collectors, and so on. These groups are outlined in
            more detail on&nbsp;
            <a className="hyperlink" href="/support/allocation" target="_blank">
              this page
            </a>
            . Within these groups, we try to assign pre-orders as fairly as
            possible, giving priority to those who have missed out on editions
            they've applied for in the past, and those who apply early.
          </Trans>
        </p>
        <p className="mb-6">
          {t('subscriptionCta.preOrder.learnMoreDialog.paragraph4')}
        </p>
        {showDialog && (
          <PreAuthDrawButton
            buttonLocation="mainCta"
            drawWithFrames={mainDraw}
            hasCheckoutParam={hasCheckoutParam}
            startDrawFlow={() => {
              startFlow({
                ...flowConfig,
                onFlowComplete: handleFlowClose,
              });
            }}
          />
        )}
      </SimpleDialog>
      <AnalyticsFlowProvider
        name="draw"
        properties={{
          'product title': activeDraw?.product.title,
          'artist name': renderShopifyArtistName(
            activeDraw?.product.artists[0].name ?? '',
          ),
          'product price': activeDraw?.product.price,
          'product can be framed': Boolean(activeDraw?.frames?.length),
          'product frame upgrade available': Boolean(activeDraw?.frames),
          'product frame chosen': Boolean(customerSelectedFrame),
          ecommerce: {
            currency,
            transaction_id: entryIntentResponse.intent?.id,
            value: activeDraw?.product.price ?? '',
            items: [
              {
                item_id: activeDraw?.product.shopifyId,
                item_name: activeDraw?.product.title,
                item_brand: formatArtistNames(
                  activeDraw?.product.artistIds ?? [],
                ),
                item_category: activeDraw?.product.medium,
                price: activeDraw?.product.price,
                quantity: 1,
              },
            ],
          },
        }}
      >
        {() =>
          setupSecret && <DialogFlowWithStripe clientSecret={setupSecret} />
        }
      </AnalyticsFlowProvider>
    </>
  );
};
